import { useState, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import {
  FileText, Send, Eye, X, Plus, Trash2,
  Sunrise, Apple, Sun, Coffee, Moon, StickyNote, CheckSquare, CalendarDays, Download, Loader2,
} from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { searchMedicines, type MedicineSuggestion } from "@/data/medicineDb";

type PractitionerType = "dietitian" | "doctor";

/* ── Shared types ── */
interface MealItem {
  id: string;
  item: string;
  timing?: "before_meal" | "after_meal";
}
interface PrescriptionSection {
  key: string;
  label: string;
  labelBn: string;
  icon: React.ElementType;
  items: MealItem[];
}

/* ── Doctor-specific medicine type ── */
const DOSE_OPTIONS = ["0", "1/2", "1", "2"];
interface Medicine {
  id: string;
  name: string;
  morning: string;
  afternoon: string;
  evening: string;
  timing: "before_meal" | "after_meal";
}

interface PrescriptionFormProps {
  practitionerType: PractitionerType;
  patientName: string;
  patientNameBn?: string;
  practitionerName: string;
  onSend: (prescription: { sections: any[]; specialNotes: string; tasks: string[]; durationDays: number }) => void;
  onClose: () => void;
}

/* ── Dietitian section templates ── */
const dietitianSections: Omit<PrescriptionSection, "items">[] = [
  { key: "breakfast", label: "Breakfast", labelBn: "সকালের নাস্তা", icon: Sunrise },
  { key: "midmorning", label: "Mid-morning Snack", labelBn: "মিড-মর্নিং স্ন্যাক", icon: Apple },
  { key: "lunch", label: "Lunch", labelBn: "দুপুরের খাবার", icon: Sun },
  { key: "afternoon", label: "Afternoon Snack", labelBn: "বিকেলের নাস্তা", icon: Coffee },
  { key: "dinner", label: "Dinner", labelBn: "রাতের খাবার", icon: Moon },
];

/* ── Helpers ── */
const dosageStr = (m: Medicine) => `${m.morning}+${m.afternoon}+${m.evening}`;
const timingLabel = (timing: string, bn: boolean) =>
  timing === "before_meal" ? (bn ? "খাবারের আগে" : "Before Meal") : (bn ? "খাবারের পরে" : "After Meal");

const newMedicine = (): Medicine => ({
  id: crypto.randomUUID(),
  name: "",
  morning: "1",
  afternoon: "1",
  evening: "1",
  timing: "after_meal",
});

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const PrescriptionForm = ({
  practitionerType, patientName, patientNameBn, practitionerName, onSend, onClose,
}: PrescriptionFormProps) => {
  const { language } = useLanguage();
  const bn = language === "bn";
  const isDoctor = practitionerType === "doctor";

  /* ── Doctor state ── */
  const [medicines, setMedicines] = useState<Medicine[]>([newMedicine()]);
  const [activeSugId, setActiveSugId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<MedicineSuggestion[]>([]);

  /* ── Dietitian state ── */
  const [sections, setSections] = useState<PrescriptionSection[]>(
    dietitianSections.map((s) => ({ ...s, items: [{ id: crypto.randomUUID(), item: "" }] }))
  );

  /* ── Shared state ── */
  const [specialNotes, setSpecialNotes] = useState("");
  const [durationDays, setDurationDays] = useState(7);
  const [showPreview, setShowPreview] = useState(false);

  /* ── Doctor helpers ── */
  const updateMedicine = (id: string, field: keyof Medicine, value: string) =>
    setMedicines((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  const removeMedicine = (id: string) =>
    setMedicines((prev) => (prev.length > 1 ? prev.filter((m) => m.id !== id) : prev));
  const filledMedicines = medicines.filter((m) => m.name.trim());
  // PDF display: keep original 1+0+1 format
  const doctorPdfTasks = filledMedicines.map(
    (m) => `${m.name.trim()} — ${dosageStr(m)} (${timingLabel(m.timing, false)})`
  );
  // To-do tasks: split by active time slot
  const doctorTasks = filledMedicines.flatMap((m) => {
    const timing = timingLabel(m.timing, false);
    const slots: string[] = [];
    if (parseInt(m.morning) > 0)   slots.push(`[morning] ${m.name.trim()} (${timing})`);
    if (parseInt(m.afternoon) > 0) slots.push(`[midday] ${m.name.trim()} (${timing})`);
    if (parseInt(m.evening) > 0)   slots.push(`[evening] ${m.name.trim()} (${timing})`);
    if (slots.length === 0)        slots.push(`${m.name.trim()} — ${dosageStr(m)} (${timing})`);
    return slots;
  });
  const doctorSections = [{ key: "medicines", label: "Medicines", labelBn: "ওষুধ", items: filledMedicines.map((m) => ({ id: m.id, item: m.name.trim(), morning: m.morning, afternoon: m.afternoon, evening: m.evening, timing: m.timing })) }];

  /* ── Dietitian helpers ── */
  const addDietItem = (key: string) =>
    setSections((prev) => prev.map((s) => s.key === key ? { ...s, items: [...s.items, { id: crypto.randomUUID(), item: "" }] } : s));
  const removeDietItem = (key: string, itemId: string) =>
    setSections((prev) => prev.map((s) => s.key === key ? { ...s, items: s.items.filter((i) => i.id !== itemId) } : s));
  const updateDietItem = (key: string, itemId: string, field: "item" | "timing", value: string) =>
    setSections((prev) => prev.map((s) => s.key === key ? { ...s, items: s.items.map((i) => i.id === itemId ? { ...i, [field]: value } : i) } : s));
  const filledSections = sections.filter((s) => s.items.some((i) => i.item.trim()));
  const dietTasks = filledSections.flatMap((s) =>
    s.items.filter((i) => i.item.trim()).map((i) => {
      const tl = i.timing === "before_meal" ? (bn ? " (খাবারের আগে)" : " (Before meal)") : i.timing === "after_meal" ? (bn ? " (খাবারের পরে)" : " (After meal)") : "";
      return `${bn ? (s as any).labelBn : s.label}: ${i.item}${tl}`;
    })
  );

  const canPreview = isDoctor ? filledMedicines.length > 0 : dietTasks.length > 0;

  const handleSend = () => {
    if (isDoctor) {
      onSend({ sections: doctorSections, specialNotes, tasks: doctorTasks, durationDays });
    } else {
      onSend({ sections: filledSections, specialNotes, tasks: dietTasks, durationDays });
    }
  };

  if (showPreview) {
    return (
      <PreviewPane
        bn={bn} isDoctor={isDoctor} practitionerName={practitionerName}
        patientName={patientName} patientNameBn={patientNameBn}
        medicines={filledMedicines} filledSections={filledSections}
        specialNotes={specialNotes} durationDays={durationDays}
        tasks={isDoctor ? doctorPdfTasks : dietTasks}
        onEdit={() => setShowPreview(false)} onSend={handleSend}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-extrabold text-sm flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          {bn ? "প্রেসক্রিপশন লিখুন" : "Write Prescription"}
        </h4>
        <button onClick={onClose}><X className="h-4 w-4 text-muted-foreground" /></button>
      </div>

      <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">

        {/* ── DOCTOR: Medicine List ── */}
        {isDoctor && (
          <div className="space-y-2">
            {medicines.map((med, idx) => (
              <div key={med.id} className="bg-muted/30 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-primary w-4 shrink-0">{idx + 1}.</span>
                  {/* Medicine name with autocomplete */}
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={med.name}
                      onChange={(e) => {
                        updateMedicine(med.id, "name", e.target.value);
                        const q = e.target.value;
                        const results = searchMedicines(q);
                        setSuggestions(results);
                        setActiveSugId(q.length >= 2 && results.length > 0 ? med.id : null);
                      }}
                      onFocus={() => {
                        if (med.name.length >= 2) {
                          const results = searchMedicines(med.name);
                          setSuggestions(results);
                          setActiveSugId(results.length > 0 ? med.id : null);
                        }
                      }}
                      onBlur={() => setTimeout(() => setActiveSugId(null), 150)}
                      placeholder={bn ? "ওষুধের নাম / ব্র্যান্ড লিখুন..." : "Brand or generic name..."}
                      className="w-full px-3 py-2 rounded-lg bg-background text-foreground text-xs border border-border/30 outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-primary/30"
                    />
                    {/* Suggestions dropdown */}
                    {activeSugId === med.id && suggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden">
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onMouseDown={() => {
                              const filled = s.strength ? `${s.generic} ${s.strength}` : s.generic;
                              updateMedicine(med.id, "name", filled);
                              setActiveSugId(null);
                              setSuggestions([]);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-primary/10 flex items-center justify-between gap-2 border-b border-border/20 last:border-0"
                          >
                            <span className="text-xs font-bold text-foreground">{s.brand}</span>
                            <span className="text-[10px] text-primary font-bold">→ {s.generic} {s.strength}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {medicines.length > 1 && (
                    <button onClick={() => removeMedicine(med.id)} className="text-destructive p-1.5 rounded-lg hover:bg-destructive/10">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-muted-foreground font-bold">{bn ? "ডোজ:" : "Dose:"}</span>
                  {(["morning", "afternoon", "evening"] as const).map((period, i) => (
                    <div key={period} className="flex items-center gap-1">
                      {i > 0 && <span className="text-xs font-bold text-muted-foreground">+</span>}
                      <select
                        value={med[period]}
                        onChange={(e) => updateMedicine(med.id, period, e.target.value)}
                        className="px-2 py-1.5 rounded-lg bg-background text-foreground text-xs font-bold border border-border/30 outline-none focus:ring-1 focus:ring-primary/30 w-14"
                      >
                        {DOSE_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  ))}
                  <select
                    value={med.timing}
                    onChange={(e) => updateMedicine(med.id, "timing", e.target.value)}
                    className="ml-auto px-2 py-1.5 rounded-lg bg-background text-foreground text-[10px] font-bold border border-border/30 outline-none focus:ring-1 focus:ring-primary/30"
                  >
                    <option value="after_meal">{bn ? "খাবারের পরে" : "After Meal"}</option>
                    <option value="before_meal">{bn ? "খাবারের আগে" : "Before Meal"}</option>
                  </select>
                </div>

                {med.name.trim() && (
                  <p className="text-[10px] font-bold text-primary bg-primary/5 px-2 py-1 rounded-lg">
                    {med.name.trim()} — {dosageStr(med)} ({timingLabel(med.timing, bn)})
                  </p>
                )}
              </div>
            ))}

            <button
              onClick={() => setMedicines((prev) => [...prev, newMedicine()])}
              className="w-full py-2.5 rounded-xl border-2 border-dashed border-primary/30 text-primary text-xs font-bold flex items-center justify-center gap-1.5 hover:border-primary/60 hover:bg-primary/5 transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              {bn ? "আরো ওষুধ যোগ করুন" : "Add Medicine"}
            </button>
          </div>
        )}

        {/* ── DIETITIAN: Sections ── */}
        {!isDoctor && sections.map((section) => {
          const IconComp = section.icon;
          return (
            <div key={section.key} className="bg-muted/30 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-extrabold text-xs flex items-center gap-1.5">
                  <IconComp className="h-3.5 w-3.5 text-primary" />
                  {bn ? section.labelBn : section.label}
                </p>
                <button onClick={() => addDietItem(section.key)} className="text-primary p-1 rounded-lg hover:bg-primary/10">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              {section.items.map((item) => (
                <div key={item.id} className="flex gap-1.5 mb-1.5">
                  <input
                    type="text"
                    value={item.item}
                    onChange={(e) => updateDietItem(section.key, item.id, "item", e.target.value)}
                    placeholder={bn ? "খাবারের আইটেম..." : "Food item / instruction..."}
                    className="flex-1 px-3 py-2 rounded-lg bg-background text-foreground text-xs border border-border/30 outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-primary/30"
                  />
                  {section.items.length > 1 && (
                    <button onClick={() => removeDietItem(section.key, item.id)} className="text-destructive p-1.5 rounded-lg hover:bg-destructive/10">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {/* ── Duration ── */}
        <div className="bg-muted/30 rounded-xl p-3">
          <p className="font-extrabold text-xs mb-2 flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5 text-primary" />
            {bn ? "কত দিনের জন্য" : "Duration (Days)"}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {[3, 5, 7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDurationDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${durationDays === d ? "bg-primary text-primary-foreground" : "bg-background text-foreground border border-border/30 hover:border-primary/30"}`}
              >
                {d}
              </button>
            ))}
            <input
              type="number" min={1} max={365} value={durationDays}
              onChange={(e) => setDurationDays(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 px-2 py-1.5 rounded-lg bg-background text-foreground text-xs border border-border/30 outline-none text-center focus:ring-1 focus:ring-primary/30"
            />
            <span className="text-xs text-muted-foreground">{bn ? "দিন" : "days"}</span>
          </div>
        </div>

        {/* ── Special Notes ── */}
        <div className="bg-warning/5 rounded-xl p-3 border border-warning/20">
          <p className="font-extrabold text-xs mb-2 flex items-center gap-1.5">
            <StickyNote className="h-3.5 w-3.5 text-warning" />
            {bn ? "বিশেষ নোট" : "Special Notes"}
          </p>
          <textarea
            value={specialNotes}
            onChange={(e) => setSpecialNotes(e.target.value)}
            placeholder={bn ? "বিশেষ নির্দেশনা লিখুন..." : "Any special instructions..."}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-background text-foreground text-xs border border-border/30 outline-none placeholder:text-muted-foreground resize-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
      </div>

      <button
        onClick={() => setShowPreview(true)}
        disabled={!canPreview}
        className="w-full gradient-accent text-accent-foreground py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Eye className="h-4 w-4" />
        {bn ? "প্রিভিউ ও পাঠান" : "Preview & Send"}
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════
   PREVIEW PANE
════════════════════════════════════════════ */
interface PreviewPaneProps {
  bn: boolean; isDoctor: boolean; practitionerName: string;
  patientName: string; patientNameBn?: string;
  medicines: Medicine[]; filledSections: PrescriptionSection[];
  specialNotes: string; durationDays: number; tasks: string[];
  onEdit: () => void; onSend: () => void;
}

const PreviewPane = ({
  bn, isDoctor, practitionerName, patientName, patientNameBn,
  medicines, filledSections, specialNotes, durationDays, tasks, onEdit, onSend,
}: PreviewPaneProps) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const sendingRef = useRef(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`prescription-${patientName}-${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")}.pdf`);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="glass rounded-xl overflow-hidden border border-border/50 max-h-[60vh] overflow-y-auto">

        {/* ── Preview content captured for PDF ── */}
        <div ref={previewRef} style={{ background: "#ffffff" }}>

          {/* Header */}
          <div className="bg-green-600 p-3 flex items-center gap-2">
            <img src="/logo.png" alt="Sebaco" className="h-6 w-6 rounded object-contain" />
            <span className="font-extrabold text-xs text-white">Sebaco</span>
            <span className="text-[10px] text-white/70 ml-1">
              {isDoctor ? (bn ? "ডাক্তারের প্রেসক্রিপশন" : "Doctor's Prescription") : (bn ? "পুষ্টি পরিকল্পনা" : "Nutrition Plan")}
            </span>
          </div>

          <div className="p-4 space-y-3 bg-white">
            {/* Doctor / Patient / Date row */}
            <div className="flex justify-between text-[10px] border-b border-gray-200 pb-2">
              <div>
                <span className="font-bold text-gray-500">{isDoctor ? (bn ? "ডাক্তার:" : "Doctor:") : (bn ? "ডায়েটিশিয়ান:" : "Dietitian:")}</span>{" "}
                <b className="text-gray-800">{practitionerName}</b>
              </div>
              <div>
                <span className="font-bold text-gray-500">{bn ? "রোগী:" : "Patient:"}</span>{" "}
                <b className="text-gray-800">{bn && patientNameBn ? patientNameBn : patientName}</b>
              </div>
            </div>
            <div className="flex justify-between items-center text-[10px] text-gray-500">
              <span>{new Date().toLocaleDateString(bn ? "bn-BD" : "en-US", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span className="font-bold text-green-700 inline-flex items-center gap-1">
                <CalendarDays className="h-3 w-3 inline-block" />
                {durationDays} {bn ? "দিন" : "days"}
              </span>
            </div>

            {/* ── DOCTOR: Rx medicine list ── */}
            {isDoctor && (
              <div>
                <p className="text-sm font-extrabold text-gray-800 mb-2 border-b border-gray-200 pb-1">℞</p>
                <div className="space-y-2">
                  {medicines.map((med, idx) => (
                    <div key={med.id} className="flex items-center gap-2 text-xs text-gray-800">
                      <span className="font-extrabold text-green-700 w-4 shrink-0">{idx + 1}.</span>
                      <span className="flex-1 font-semibold">{med.name}</span>
                      <span className="font-extrabold text-gray-700 whitespace-nowrap shrink-0">
                        {dosageStr(med)}
                      </span>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-green-100 text-green-700 font-bold whitespace-nowrap shrink-0 leading-none">
                        {timingLabel(med.timing, bn)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── DIETITIAN: Sections ── */}
            {!isDoctor && filledSections.map((section) => {
              const IconComp = section.icon;
              return (
                <div key={section.key} className="bg-gray-50 rounded-lg p-3">
                  <p className="font-extrabold text-xs mb-1.5 flex items-center gap-1.5 text-gray-700">
                    <IconComp className="h-3.5 w-3.5 text-green-600" />
                    {bn ? section.labelBn : section.label}
                  </p>
                  {section.items.filter((i) => i.item.trim()).map((item) => (
                    <div key={item.id} className="text-xs flex items-center gap-1.5 mb-0.5 text-gray-700">
                      <span className="text-green-600">•</span>
                      <span>{item.item}</span>
                      {item.timing && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">
                          {item.timing === "before_meal" ? (bn ? "খাবারের আগে" : "Before meal") : (bn ? "খাবারের পরে" : "After meal")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Special Notes */}
            {specialNotes.trim() && (
              <div className="bg-yellow-50 rounded-lg p-3 border-l-4 border-yellow-400">
                <p className="font-extrabold text-xs mb-1 flex items-center gap-1.5 text-yellow-700">
                  <StickyNote className="h-3.5 w-3.5" />
                  {bn ? "বিশেষ নোট" : "Special Notes"}
                </p>
                <p className="text-xs text-gray-700 whitespace-pre-line">{specialNotes}</p>
              </div>
            )}

            {/* Signature line */}
            <div className="border-t border-gray-200 pt-3 flex justify-end">
              <div className="text-center">
                <div className="w-32 border-b border-gray-400 mb-1" />
                <p className="text-[10px] text-gray-500">{practitionerName}</p>
                <p className="text-[9px] text-gray-400">{isDoctor ? (bn ? "ডাক্তারের স্বাক্ষর" : "Doctor's Signature") : (bn ? "ডায়েটিশিয়ানের স্বাক্ষর" : "Dietitian's Signature")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={onEdit} className="flex-1 bg-secondary text-secondary-foreground py-2.5 rounded-xl font-bold text-sm">
          {bn ? "সম্পাদনা" : "Edit"}
        </button>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="px-4 py-2.5 rounded-xl font-bold text-sm bg-muted text-foreground flex items-center gap-1.5 hover:bg-muted/70 transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          PDF
        </button>
        <motion.button
          onClick={() => { if (!sendingRef.current) { sendingRef.current = true; onSend(); } }}
          className="flex-1 gradient-primary text-primary-foreground py-2.5 rounded-xl font-bold text-sm shadow-glow flex items-center justify-center gap-2 disabled:opacity-60"
          whileTap={{ scale: 0.97 }}
        >
          <Send className="h-4 w-4" />
          {bn ? "পাঠান" : "Send"}
        </motion.button>
      </div>
    </div>
  );
};

export default PrescriptionForm;
